import { useCallback, useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { SelectOption } from '@kep-ui-kit/ui-components';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import moment from 'moment';

import { apiEndpoints, downloadURI, subscriptionStatuses } from '@selfserve/react-utils';
import { useCustomSearchParams } from '@selfserve/react-hooks';
import { StyledButton } from '@selfserve/react-ui';

import { subscriptionSortingValues } from '../constants/subscription-sorting-values';
import { subscriptionStatusesTexts } from '../constants/status-texts';
import { subscriptionStatusColors } from '../constants/status-colors';

import { AdminSearchInput } from './admin-search-input';
import {
  StyledExportButton,
  StyledStatus,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledTr,
} from '../ui/admin-styled-components';
import { TableHeaderButton } from '../ui/table-header-button';
import { AdminPlansTooltip } from '../ui/admin-plans-tooltip';
import { BeSearchButton } from '../ui/be-search-button';
import { AdminPaginationController } from './admin-pagination-controller';
import { AdminPlanExtendModal } from './admin-plan-extend-modal';
import { AdminStatusFilter } from './admin-status-filter';
import { AdminSummaryChartModal } from './admin-summary-chart-modal';

const StyledSubscriptionsListContainer = styled('div')`
  height: calc(100% - 110px);
  margin-top: 24px;
`;

const StyledSearchContainer = styled('div')`
  width: 100%;
  display: flex;
`;

const StyledTrackHorizontal = styled('div')`
  height: 10px !important;
  z-index: 2;
  background-color: #fff;
  bottom: 0;
  width: 100%;
  div {
    height: 6px !important;
    margin-top: 4px;
  }
`;

const StyledTrackVertical = styled('div')`
  width: 10px !important;
  z-index: 2;
  background-color: #fff;
  right: 0;
  height: 100%;
  div {
    width: 6px !important;
    margin-left: 4px;
  }
`;

const StyledScrollbarsContainer = styled('div')`
  margin-top: 24px;
  height: calc(100% - 32px);
`;

const StyledSummaryButton = styled(StyledButton)`
  margin-left: auto;
  margin-right: 16px;
`;

interface Props {
  subscriptions: any[];
  summary: any;
  refetch: () => void;
  handleSetBeFilter: (value: string) => boolean;
}

export const AdminSubscriptionsList = ({
  subscriptions,
  summary,
  refetch,
  handleSetBeFilter,
}: Props) => {
  const { t } = useTranslation();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [searchByBe, setSearchByBe] = useState(false);
  const [search, setSearch] = useState('');
  const [query] = useCustomSearchParams();
  const [extendPlans, setExtendPlans] = useState<SelectOption[]>([]);

  useEffect(() => {
    const searchValue = query['search'] as string;
    if (searchValue) {
      const isSearchByBe = handleSetBeFilter(searchValue);
      setSearchByBe(isSearchByBe);
    }
  }, []);

  useEffect(() => {
    if (searchByBe && search !== query['search']) setSearchByBe(false);
  }, [search]);

  const handleSetIsSummaryOpen = useCallback(
    (value: boolean) => () => setIsSummaryOpen(value),
    []
  );

  const handleDownloadCsv = useCallback(
    () => downloadURI(apiEndpoints.downloadSubscriptionCsv),
    []
  );

  const handleOpenExtendModal = useCallback(
    (plans: any[]) => () => {
      const filteredPlans = plans?.filter(
        ({ status }: any) => status === subscriptionStatuses.IN_TRIAL
      );
      if (filteredPlans?.length) {
        setExtendPlans(
          plans.map(({ plan: { displayName, name }, _id }) => ({
            text: displayName || name,
            value: _id,
          }))
        );
      }
    },
    []
  );

  const handleCloseExtendModal = useCallback(() => {
    setExtendPlans([]);
  }, []);

  const handleSearchButtonClick = useCallback(() => {
    const isSearchByBe = handleSetBeFilter(search);
    setSearchByBe(isSearchByBe);
  }, [search]);

  const finalizedList = useMemo(
    () =>
      subscriptions?.filter(({ items }: any) => {
        const searchValue = search?.trim()?.toLowerCase();
        const { partner_name, partner_email, partner_id } = (items || [])[0] || {};
        if (search && !searchByBe) {
          return (
            partner_name?.toLowerCase()?.includes(searchValue) ||
            partner_email?.toLowerCase()?.includes(searchValue) ||
            String(partner_id)?.includes(search)
          );
        }
        return true;
      }),
    [subscriptions, search, searchByBe]
  );

  return (
    <StyledSubscriptionsListContainer>
      <StyledSearchContainer>
        <AdminSearchInput value={search} onChange={setSearch} />
        <BeSearchButton onClick={handleSearchButtonClick} />
        <StyledSummaryButton
          size="small"
          onClick={handleSetIsSummaryOpen(true)}
          withoutMargin
        >
          {t('common.summary')}
        </StyledSummaryButton>
        <StyledExportButton
          onClick={handleDownloadCsv}
          withoutAutoMargin
          withoutMargin
        >
          {t('common.export')}
        </StyledExportButton>
      </StyledSearchContainer>
      <StyledScrollbarsContainer>
        <Scrollbars
          universal
          height="100%"
          renderTrackHorizontal={(props) => (
            <StyledTrackHorizontal {...props} />
          )}
          renderTrackVertical={(props) => <StyledTrackVertical {...props} />}
        >
          <StyledTable withoutMargin>
            <thead>
              <StyledTr sticky>
                <TableHeaderButton value={subscriptionSortingValues.NAME}>
                  {t('common.customer-name')}
                </TableHeaderButton>
                <TableHeaderButton value={subscriptionSortingValues.PARTNER_ID}>
                  {t('common.partner-id')}
                </TableHeaderButton>
                <StyledTh>{t('common.mrr')}</StyledTh>
                <TableHeaderButton value={subscriptionSortingValues.ITEMS}>
                  {t('common.admin-subscriptions')}
                </TableHeaderButton>
                <AdminStatusFilter
                  value={subscriptionSortingValues.STATUS}
                  statuses={Object.entries(subscriptionStatusesTexts).map(([value, text]) => ({
                    text,
                    value,
                    className: subscriptionStatusColors[value],
                  }))}
                />
                <TableHeaderButton value={subscriptionSortingValues.TRIAL_END}>
                  {t('common.trial-end')}
                </TableHeaderButton>
                <TableHeaderButton
                  value={subscriptionSortingValues.LAST_MODIFIED}
                >
                  {t('common.created-on')}
                </TableHeaderButton>
              </StyledTr>
            </thead>
            <tbody>
              {finalizedList?.map((subscription: any) => {
                const { _id, items } = subscription;
                const { partner_id } = _id || {};
                const {
                  plan,
                  status,
                  created_at,
                  updated_at,
                  partner_name,
                  partner_email,
                  trial_end,
                  mrr
                } = (items || [])[0] || {};
                const isInTrial = status === subscriptionStatuses.IN_TRIAL;
                return (
                  <StyledTr
                    disabled={!isInTrial}
                    key={partner_id}
                    onClick={handleOpenExtendModal(items)}
                  >
                    <StyledTd isPairValue>
                      {partner_name || '-'}
                      <span>{partner_email || '-'}</span>
                    </StyledTd>
                    <StyledTd>{partner_id}</StyledTd>
                    <StyledTd>{mrr}</StyledTd>
                    <StyledTd>
                      <AdminPlansTooltip plans={items}>
                        {plan?.displayName || plan?.name}
                      </AdminPlansTooltip>
                    </StyledTd>
                    <StyledTd>
                      <StyledStatus
                        className={subscriptionStatusColors[status]}
                      >
                        {subscriptionStatusesTexts[status]}
                      </StyledStatus>
                    </StyledTd>
                    <StyledTd>
                      {trial_end && isInTrial
                        ? moment.unix(trial_end).format('MMM DD, YYYY')
                        : '-'}
                    </StyledTd>
                    <StyledTd>
                      {moment(created_at || updated_at).format('MMM DD, YYYY')}
                    </StyledTd>
                  </StyledTr>
                );
              })}
            </tbody>
          </StyledTable>
        </Scrollbars>
      </StyledScrollbarsContainer>
      <AdminPaginationController />
      <AdminPlanExtendModal
        open={!!extendPlans.length}
        handleClose={handleCloseExtendModal}
        options={extendPlans}
        refetch={refetch}
      />
      <AdminSummaryChartModal
        isOpen={isSummaryOpen}
        onClose={handleSetIsSummaryOpen(false)}
        summary={summary}
      />
    </StyledSubscriptionsListContainer>
  );
};
